import {
  computed,
  onBeforeUnmount,
  onMounted,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'
import { useI18n } from 'vue-i18n'
import {
  detectShortcutPlatform,
  formatShortcut,
  matchesShortcut,
  type KeyboardShortcut,
} from '../lib/keyboard-shortcut'

/** Calls `onTrigger` whenever the window sees the platform-correct chord for `shortcut`, for as long as the caller is mounted. */
export function useGlobalShortcut(shortcut: KeyboardShortcut, onTrigger: () => void): void {
  const platform = detectShortcutPlatform()

  function onKeydown(event: KeyboardEvent): void {
    if (!matchesShortcut(event, shortcut, platform)) return
    event.preventDefault()
    onTrigger()
  }

  onMounted(() => window.addEventListener('keydown', onKeydown))
  onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
}

/** The shortcut's localised key-cap label, e.g. `⌘J` or `Strg+J`. */
export function useShortcutLabel(
  shortcut: MaybeRefOrGetter<KeyboardShortcut>,
): ComputedRef<string> {
  const { t } = useI18n()
  const platform = detectShortcutPlatform()
  return computed(() =>
    formatShortcut(toValue(shortcut), platform, t('primitives.keyboard.controlKey')),
  )
}
